import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Paper, Typography, makeStyles } from '@material-ui/core';
import CircularProgress from 'components/shared/CircularProgress';
import ConfirmationButton from 'components/shared/ConfirmationButton';
import ComplaintInfo from './ComplaintInfo';
import ComplaintTimeline from './ComplaintTimeline';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const ComplaintDetail = ({
  complaint,
  isLoading,
  isUpdating,
  conflict,
  onEdit,
  onWithdraw,
  onRespond,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!complaint) {
    return null;
  }

  const canEdit = complaint.status === 'pending_host';
  const canWithdraw = !['resolved', 'withdrawn', 'accepted', 'escalated'].includes(
    complaint.status,
  );
  const canRespond = complaint.status === 'pending_guest';

  return (
    <>
      <ComplaintInfo complaint={complaint} view="guest">
        <div className={classes.actions}>
          {canEdit && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onEdit}
              disabled={isUpdating || conflict}
            >
              <FormattedMessage id="complaints.edit" defaultMessage="Edit" />
            </Button>
          )}
          {canWithdraw && (
            <ConfirmationButton
              variant="outlined"
              color="secondary"
              disabled={isUpdating || conflict}
              dialogText={intl.formatMessage({
                id: 'complaints.withdrawConfirm',
                defaultMessage:
                  'Are you sure you want to withdraw this complaint? This will unblock the booking payout.',
              })}
              onConfirm={onWithdraw}
            >
              <FormattedMessage id="complaints.withdraw" defaultMessage="Withdraw" />
            </ConfirmationButton>
          )}
        </div>
      </ComplaintInfo>

      {canRespond && (
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            <FormattedMessage
              id="complaints.counterOffer"
              defaultMessage="Host has made a counter-offer"
            />
          </Typography>
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onRespond({ action: 'accept' })}
              disabled={isUpdating || conflict}
            >
              <FormattedMessage id="complaints.acceptCounter" defaultMessage="Accept" />
            </Button>
            <ConfirmationButton
              variant="outlined"
              color="secondary"
              disabled={isUpdating || conflict}
              dialogText={intl.formatMessage({
                id: 'complaints.rejectCounterConfirm',
                defaultMessage:
                  'Rejecting the counter-offer will escalate your case to Stugknuten support.',
              })}
              onConfirm={() => onRespond({ action: 'reject' })}
            >
              <FormattedMessage id="complaints.rejectCounter" defaultMessage="Reject & escalate" />
            </ConfirmationButton>
          </div>
        </Paper>
      )}

      <Paper className={classes.paper}>
        <Typography variant="subtitle1" gutterBottom>
          <FormattedMessage id="complaints.timeline" defaultMessage="Response timeline" />
        </Typography>
        <Divider />
        <ComplaintTimeline responses={complaint.responses} />
      </Paper>
    </>
  );
};

ComplaintDetail.propTypes = {
  complaint: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    responses: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  conflict: PropTypes.bool,
  onEdit: PropTypes.func,
  onWithdraw: PropTypes.func,
  onRespond: PropTypes.func,
};

export default ComplaintDetail;
